import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  ArrowLeft,
  ArrowRight,
  Twitter,
  Facebook,
  Youtube,
  Globe,
  Sparkles,
  Heart,
  Moon,
  Sun,
  Languages,
  Send,
  ShieldCheck,
  Activity,
  GraduationCap,
} from "lucide-react";

// ==========================================
// 🌍 আপনার তথ্য (STATIC DATA)
// ==========================================
const STATIC_DATA = {
  name: "Sifat Ullah",
  domain: ".online",
  avatarUrl: "/aa.png", // ছবি public ফোল্ডারে থাকতে হবে
  socials: {
    gmail: "mailto:sifatullah.contact@gmail.com",
    twitter: "https://twitter.com/sifatullah",
    facebook: "https://facebook.com/sifatullah",
    youtube: "https://youtube.com/@sifatullah",
  },
};

// ==========================================
// 🗣️ লেখা ও অনুবাদ (GENERAL PORTFOLIO + AUSBILDUNG READY)
// ==========================================
const CONTENT = {
  en: {
    nav: ["Profile", "About Me", "Journal", "Contact"],
    tagline: "B.Sc Student & Aspiring Professional in Science.",
    bio: "Currently pursuing my Honours in Fisheries & Marine Bioscience. Passionate about biological sciences, continuous learning, and cross-cultural communication. Building a strong academic foundation for future global opportunities.",
    whyHire: "Core Professional Values",
    skills: "Skills & Competencies",
    academic: "Education History",
    available: "Open for Opportunities",
    contactTitle: "Let's Connect",
    contactSub: "sifatullah.contact@gmail.com",
    form: {
      name: "Full Name",
      email: "Your Email Address",
      message: "Hello Sifat, I would like to discuss...",
      btn: "Send Message",
    },
    footer: "© 2026 Sifat Ullah. All Rights Reserved.",
    readMore: "Read More",
    backBtn: "Back to List",
    highlights: [
      {
        text: "Dedicated student with a strong background in biological sciences.",
      },
      {
        text: "Resilient and adaptable to new academic and cultural environments.",
      },
      {
        text: "Multilingual: Fluent in Bengali, English, and learning German.",
      },
      {
        text: "Committed to high standards of ethics and professional discipline.",
      },
    ],
    education: [
      {
        title: "B.Sc in Fisheries & Marine Bioscience",
        result: "Running (1st Year)",
        org: "Gopalganj Science & Technology Univ.",
        year: "2024 - Present",
      },
      {
        title: "HSC (Alim Examination)",
        result: "Highest Distinction",
        org: "Islamic Studies Institute",
        year: "2021-22",
      },
      {
        title: "SSC (Dakhil Examination)",
        result: "Highest Distinction",
        org: "Board Dhaka",
        year: "2019-20",
      },
    ],
    languages: [
      {
        name: "German (Deutsch)",
        level: "Level A2 (Intensive Learning)",
        percent: 50,
      },
      {
        name: "English",
        level: "Level B2 (Fluent Communication)",
        percent: 85,
      },
      { name: "Bengali", level: "Native Proficiency", percent: 100 },
    ],
    journal: [
      {
        id: 1,
        category: "Science",
        title: "The Pursuit of Knowledge",
        excerpt: "Why continuous learning is the key to professional growth...",
        content:
          "Science is not just about facts; it's about curiosity and understanding the world. My studies in Marine Bioscience have taught me to be analytical, observant, and patient—skills that are essential in any professional field.",
      },
      {
        id: 2,
        category: "Growth",
        title: "Adapting to New Cultures",
        excerpt: "The importance of language and cultural integration...",
        content:
          "Learning German is more than just grammar; it's about connecting with a new way of thinking. I believe that being open to new cultures makes us better professionals and more empathetic human beings.",
      },
      {
        id: 3,
        category: "Ethics",
        title: "Professional Discipline",
        excerpt: "Building a career on strong ethical foundations...",
        content:
          "Whether in a laboratory or a workplace, discipline and ethics are the backbone of success. I am committed to maintaining honesty, punctuality, and hard work in every task I undertake.",
      },
    ],
  },
  de: {
    nav: ["Profil", "Über Mich", "Journal", "Kontakt"],
    tagline: "B.Sc-Student & Angehender Fachmann für Wissenschaft.",
    bio: "Derzeit Studium (B.Sc) in Fischerei & Meeresbiowissenschaften. Begeistert von Biowissenschaften, ständiger Weiterbildung und interkultureller Kommunikation. Ich baue ein starkes akademisches Fundament für zukünftige globale Chancen.",
    whyHire: "Berufliche Kernwerte",
    skills: "Fähigkeiten & Kompetenzen",
    academic: "Akademischer Werdegang",
    available: "Offen für Möglichkeiten",
    contactTitle: "Kontaktieren Sie mich",
    contactSub: "sifatullah.contact@gmail.com",
    form: {
      name: "Vollständiger Name",
      email: "Ihre E-Mail-Adresse",
      message: "Hallo Sifat, ich möchte gerne besprechen...",
      btn: "Nachricht Senden",
    },
    footer: "© 2026 Sifat Ullah. Alle Rechte vorbehalten.",
    readMore: "Mehr lesen",
    backBtn: "Zurück",
    highlights: [
      {
        text: "Engagierter Student mit starkem Hintergrund in Biowissenschaften.",
      },
      { text: "Belastbar und anpassungsfähig an neue akademische Umgebungen." },
      {
        text: "Mehrsprachig: Fließend in Bengali, Englisch und lerne Deutsch.",
      },
      { text: "Verpflichtet zu hohen Standards in Ethik und Disziplin." },
    ],
    education: [
      {
        title: "B.Sc in Fischerei & Meeresbiologie",
        result: "Laufend (1. Jahr)",
        org: "Gopalganj Science & Technology Univ.",
        year: "2024 - Heute",
      },
      {
        title: "HSC (Alim Prüfung)",
        result: "Mit Auszeichnung",
        org: "Institut für Islamische Studien",
        year: "2021-22",
      },
      {
        title: "SSC (Dakhil Prüfung)",
        result: "Mit Auszeichnung",
        org: "Bildungsboard Dhaka",
        year: "2019-20",
      },
    ],
    languages: [
      { name: "Deutsch", level: "Niveau A2 (Intensives Lernen)", percent: 50 },
      {
        name: "Englisch",
        level: "Niveau B2 (Fließende Kommunikation)",
        percent: 85,
      },
      { name: "Bengali", level: "Muttersprache", percent: 100 },
    ],
    journal: [
      {
        id: 1,
        category: "Wissenschaft",
        title: "Das Streben nach Wissen",
        excerpt: "Warum ständiges Lernen der Schlüssel zum Wachstum ist...",
        content:
          "Wissenschaft besteht nicht nur aus Fakten; es geht um Neugier. Mein Studium der Meeresbiowissenschaften hat mich gelehrt, analytisch, aufmerksam und geduldig zu sein – Fähigkeiten, die in jedem Berufsfeld unerlässlich sind.",
      },
      {
        id: 2,
        category: "Wachstum",
        title: "Anpassung an neue Kulturen",
        excerpt: "Die Bedeutung von Sprache und kultureller Integration...",
        content:
          "Deutsch zu lernen ist mehr als nur Grammatik; es geht darum, sich mit einer neuen Denkweise zu verbinden. Ich glaube, dass Offenheit für neue Kulturen uns zu besseren Profis macht.",
      },
      {
        id: 3,
        category: "Ethik",
        title: "Berufliche Disziplin",
        excerpt: "Karriereaufbau auf starken ethischen Grundlagen...",
        content:
          "Ob im Labor oder am Arbeitsplatz, Disziplin und Ethik sind das Rückgrat des Erfolgs. Ich verpflichte mich zu Ehrlichkeit, Pünktlichkeit und harter Arbeit bei jeder Aufgabe.",
      },
    ],
  },
};

const floatTransition = { type: "spring", stiffness: 400, damping: 17 };
const hoverEffect = { y: -5, scale: 1.02 };
const tapEffect = { scale: 0.95 };
const HIGHLIGHT_ICONS = [
  <Heart size={18} key="1" />,
  <Activity size={18} key="2" />,
  <Globe size={18} key="3" />,
  <ShieldCheck size={18} key="4" />,
];

const App = () => {
  const [activeTab, setActiveTab] = useState("home");
  const [isDark, setIsDark] = useState(true);
  const [lang, setLang] = useState("en");
  const [selectedJournal, setSelectedJournal] = useState(null);
  const t = CONTENT[lang];

  return (
    <div
      className={`min-h-screen font-sans transition-colors duration-1000 ${
        isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"
      } flex items-center justify-center p-4 relative overflow-hidden`}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-0 left-0 w-[600px] h-[600px] bg-teal-500/10 blur-[150px] rounded-full"
        />
        <motion.div
          animate={{ x: [0, -100, 0], y: [0, -50, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-blue-500/10 blur-[150px] rounded-full"
        />
      </div>

      <div className="fixed top-6 right-6 z-50 flex gap-3">
        <motion.button
          whileHover={hoverEffect}
          whileTap={tapEffect}
          transition={floatTransition}
          onClick={() => setLang(lang === "en" ? "de" : "en")}
          className="p-3 bg-white/10 backdrop-blur-3xl border border-white/20 rounded-2xl shadow-xl text-teal-500 font-bold"
        >
          {lang === "en" ? "DE" : "EN"}
        </motion.button>
        <motion.button
          whileHover={hoverEffect}
          whileTap={tapEffect}
          transition={floatTransition}
          onClick={() => setIsDark(!isDark)}
          className="p-3 bg-white/10 backdrop-blur-3xl border border-white/20 rounded-2xl shadow-xl text-teal-500"
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </motion.button>
      </div>

      <motion.div
        layout
        className={`relative z-10 w-full max-w-5xl h-[90vh] md:h-[800px] backdrop-blur-3xl border rounded-[40px] flex flex-col shadow-2xl overflow-hidden transition-all duration-700 ${
          isDark
            ? "bg-slate-900/40 border-slate-800"
            : "bg-white/90 border-slate-200/50 shadow-xl shadow-teal-900/5"
        }`}
      >
        <header className="pt-8 flex flex-col items-center shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 mb-6"
          >
            <Globe className="text-teal-500" size={24} />
            <h2 className="text-xl font-black tracking-tighter uppercase">
              {STATIC_DATA.name}
              <span className="text-teal-500">{STATIC_DATA.domain}</span>
            </h2>
          </motion.div>
          <nav className="flex bg-black/5 dark:bg-white/5 p-1 rounded-2xl border border-white/10 gap-1 mb-6">
            {["home", "portfolio", "journal", "contact"].map((id, i) => (
              <motion.button
                key={id}
                onClick={() => {
                  setActiveTab(id);
                  setSelectedJournal(null);
                }}
                className={`px-4 md:px-6 py-2 rounded-xl text-[10px] md:text-xs font-black tracking-widest transition-all relative uppercase ${
                  activeTab === id ? "text-white" : "text-slate-500"
                }`}
              >
                {activeTab === id && (
                  <motion.div
                    layoutId="nav-bg"
                    className="absolute inset-0 bg-teal-500 rounded-xl shadow-lg"
                  />
                )}
                <span className="relative z-10">{t.nav[i]}</span>
              </motion.button>
            ))}
          </nav>
        </header>

        <main className="flex-grow p-6 md:p-12 overflow-y-auto custom-scrollbar">
          <AnimatePresence mode="wait">
            {activeTab === "home" && (
              <motion.div
                key="home"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="h-full flex flex-col items-center justify-center text-center gap-6 pt-4 md:pt-0"
              >
                <div className="relative">
                  <div className="absolute inset-[-20px] bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full blur-[40px] opacity-40 animate-pulse" />
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="relative w-48 h-48 md:w-64 md:h-64 rounded-full overflow-visible flex items-center justify-center"
                  >
                    <div className="absolute inset-0 rounded-full border-2 border-cyan-400/30 shadow-[0_0_30px_rgba(6,182,212,0.5)]" />
                    <div className="w-full h-full rounded-full overflow-hidden relative z-10">
                      <img
                        src={STATIC_DATA.avatarUrl}
                        alt={STATIC_DATA.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </motion.div>
                </div>
                <div className="max-w-xl space-y-4 z-20">
                  <h1 className="text-3xl md:text-5xl font-black leading-tight tracking-tighter drop-shadow-sm">
                    {t.tagline}
                  </h1>
                  <p className="text-sm md:text-base font-medium opacity-60 max-w-md mx-auto leading-relaxed">
                    {t.bio}
                  </p>
                </div>
              </motion.div>
            )}
            {activeTab === "portfolio" && (
              <motion.div
                key="portfolio"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-12 pb-10"
              >
                <div
                  className={`p-8 rounded-[32px] border ${
                    isDark
                      ? "bg-white/5 border-white/5"
                      : "bg-teal-50/40 border-teal-100/50"
                  }`}
                >
                  <h3 className="text-lg font-black mb-6 flex items-center gap-3 tracking-tight">
                    <Sparkles className="text-teal-500" size={20} /> {t.whyHire}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {t.highlights.map((point, i) => (
                      <motion.div
                        key={i}
                        whileHover={hoverEffect}
                        transition={floatTransition}
                        className="flex gap-4 items-center p-4 bg-white/50 dark:bg-black/20 rounded-2xl border border-white/20 hover:border-teal-500/30 transition-all group"
                      >
                        <div className="p-2 bg-teal-500/10 text-teal-500 rounded-lg group-hover:bg-teal-500 group-hover:text-white transition-colors">
                          {HIGHLIGHT_ICONS[i]}
                        </div>
                        <span className="text-sm font-bold opacity-70 leading-snug">
                          {point.text}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-10">
                  <div className="space-y-6">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40 flex items-center gap-2">
                      <GraduationCap size={14} /> {t.academic}
                    </h3>
                    {t.education.map((edu, i) => (
                      <motion.div
                        key={i}
                        whileHover={{ x: 5 }}
                        transition={floatTransition}
                        className="pl-6 border-l-2 border-teal-500/20 relative py-2"
                      >
                        <div className="absolute -left-[5px] top-4 w-2 h-2 rounded-full bg-teal-500" />
                        <h4 className="font-black text-sm">{edu.title}</h4>
                        <p className="text-xs font-bold text-teal-600 mt-1">
                          {edu.result}{" "}
                          <span className="opacity-40 font-medium ml-2">
                            [{edu.year}] • {edu.org}
                          </span>
                        </p>
                      </motion.div>
                    ))}
                  </div>
                  <div className="space-y-6">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40 flex items-center gap-2">
                      <Languages size={14} /> {t.skills}
                    </h3>
                    {t.languages.map((lang, i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex justify-between font-black text-[10px] uppercase tracking-wider">
                          <span>{lang.name}</span>
                          <span className="text-teal-500">{lang.level}</span>
                        </div>
                        <div className="h-2 bg-black/5 dark:bg-white/5 rounded-full overflow-hidden border border-white/5">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${lang.percent}%` }}
                            className="h-full bg-gradient-to-r from-teal-500 to-blue-500"
                            transition={{ duration: 1.5, ease: "easeOut" }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
            {activeTab === "journal" &&
              (selectedJournal ? (
                <motion.div
                  key="detail"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6 max-w-2xl mx-auto pb-10"
                >
                  <motion.button
                    whileHover={{ x: -5 }}
                    transition={floatTransition}
                    onClick={() => setSelectedJournal(null)}
                    className="flex items-center gap-2 text-xs font-black text-teal-500 mb-8 uppercase tracking-widest"
                  >
                    <ArrowLeft size={14} /> {t.backBtn}
                  </motion.button>
                  <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-tighter">
                    {selectedJournal.title}
                  </h2>
                  <div className="h-1.5 w-16 bg-teal-500 rounded-full mb-8" />
                  <p className="text-base md:text-lg leading-relaxed font-medium opacity-80">
                    {selectedJournal.content}
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="grid grid-cols-1 md:grid-cols-3 gap-6"
                >
                  {t.journal.map((post) => (
                    <motion.div
                      key={post.id}
                      whileHover={hoverEffect}
                      whileTap={tapEffect}
                      transition={floatTransition}
                      onClick={() => setSelectedJournal(post)}
                      className={`p-6 rounded-[24px] border cursor-pointer group flex flex-col gap-4 transition-all ${
                        isDark
                          ? "bg-white/5 border-white/5 hover:bg-teal-500/10"
                          : "bg-white border-slate-100 hover:shadow-2xl"
                      }`}
                    >
                      <span className="text-[9px] font-black px-2.5 py-1 bg-teal-500/10 text-teal-600 rounded-lg uppercase tracking-[0.15em] w-max">
                        {post.category}
                      </span>
                      <h4 className="font-black text-lg group-hover:text-teal-500 transition-colors leading-tight">
                        {post.title}
                      </h4>
                      <p className="text-xs opacity-50 line-clamp-3 leading-relaxed">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-2 text-[10px] font-black text-teal-500 mt-auto uppercase tracking-widest pt-4">
                        {t.readMore} <ArrowRight size={12} />
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              ))}
            {activeTab === "contact" && (
              <motion.div
                key="contact"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-md mx-auto pb-10"
              >
                <div className="text-center mb-10">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 5 }}
                    className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 text-white rounded-[20px] flex items-center justify-center mx-auto mb-4 shadow-xl shadow-teal-500/20"
                  >
                    <Mail size={24} />
                  </motion.div>
                  <h2 className="text-3xl font-black tracking-tight">
                    {t.contactTitle}
                  </h2>
                  <p className="text-xs font-bold opacity-40 uppercase tracking-widest mt-2">
                    {t.contactSub}
                  </p>
                </div>
                <div className="flex justify-center gap-4 mb-10">
                  <SocialBtn
                    href={STATIC_DATA.socials.gmail}
                    icon={<Mail size={20} />}
                    color="hover:bg-red-500"
                  />
                  <SocialBtn
                    href={STATIC_DATA.socials.twitter}
                    icon={<Twitter size={20} />}
                    color="hover:bg-sky-500"
                  />
                  <SocialBtn
                    href={STATIC_DATA.socials.facebook}
                    icon={<Facebook size={20} />}
                    color="hover:bg-blue-600"
                  />
                  <SocialBtn
                    href={STATIC_DATA.socials.youtube}
                    icon={<Youtube size={20} />}
                    color="hover:bg-red-600"
                  />
                </div>
                <form
                  action="https://formspree.io/f/xreeglpw"
                  method="POST"
                  className="space-y-3"
                >
                  <input
                    name="name"
                    type="text"
                    required
                    placeholder={t.form.name}
                    className={`w-full p-4 rounded-2xl border outline-none focus:ring-2 focus:ring-teal-500/30 font-bold transition-all text-sm ${
                      isDark
                        ? "bg-slate-800/50 border-slate-700"
                        : "bg-white border-slate-200"
                    }`}
                  />
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder={t.form.email}
                    className={`w-full p-4 rounded-2xl border outline-none focus:ring-2 focus:ring-teal-500/30 font-bold transition-all text-sm ${
                      isDark
                        ? "bg-slate-800/50 border-slate-700"
                        : "bg-white border-slate-200"
                    }`}
                  />
                  <textarea
                    name="message"
                    required
                    placeholder={t.form.message}
                    rows="3"
                    className={`w-full p-4 rounded-2xl border outline-none focus:ring-2 focus:ring-teal-500/30 font-bold transition-all text-sm ${
                      isDark
                        ? "bg-slate-800/50 border-slate-700"
                        : "bg-white border-slate-200"
                    }`}
                  />
                  <motion.button
                    type="submit"
                    whileHover={hoverEffect}
                    whileTap={tapEffect}
                    transition={floatTransition}
                    className="w-full bg-teal-500 text-white font-black py-4 rounded-2xl shadow-xl shadow-teal-500/20 uppercase tracking-widest text-xs flex items-center justify-center gap-2"
                  >
                    {t.form.btn} <Send size={16} />
                  </motion.button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
        <footer className="p-6 text-center text-[10px] font-bold opacity-30 tracking-widest uppercase">
          {t.available} — {t.footer}
        </footer>
      </motion.div>
    </div>
  );
};

const SocialBtn = ({ href, icon, color }) => (
  <motion.a
    whileHover={hoverEffect}
    whileTap={tapEffect}
    transition={floatTransition}
    href={href}
    target="_blank"
    rel="noreferrer"
    className={`p-3.5 bg-slate-500/10 text-slate-500 rounded-xl border border-white/5 transition-all ${color} hover:text-white shadow-sm`}
  >
    {icon}
  </motion.a>
);

export default App;
